import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { format, addDays } from 'date-fns';
import { useQueryClient } from 'react-query';
import { availabilityAPI, calendarIntegrationAPI } from '../../services/api';
import { useNotifications } from '../notifications';
import { useDashboardRefetch } from '../../hooks/useDashboard';
import { useDebouncedSave } from '../../hooks/useDebouncedSave';
import LoadingSpinner from '../LoadingSpinner';
import DesktopAvailabilityGrid from './DesktopAvailabilityGrid';
import MobileAvailabilityGrid from './MobileAvailabilityGrid';
import AvailabilityStatusChip from './AvailabilityStatusChip';
import { crewsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { TIME_SLOTS } from './AvailabilityTypes';
import { weeklyApiSlotsToGridAvailability } from '../../utils/availabilityUtils';

const AvailabilityGrid = ({ currentWeek, onWeekChange, onSaveStatusChange }) => {
  const { showToast } = useNotifications();
  const queryClient = useQueryClient();
  const { invalidateDashboard } = useDashboardRefetch();
  const [availability, setAvailabilityRaw] = useState({});
  const dirtyDayIndicesRef = useRef(new Set());
  const suppressDirtyTrackingRef = useRef(false);

  const setAvailability = useCallback((updater) => {
    setAvailabilityRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (!suppressDirtyTrackingRef.current) {
        for (let di = 0; di < 7; di++) {
          const p = prev[di] || {};
          const n = next[di] || {};
          if (JSON.stringify(p) !== JSON.stringify(n)) {
            dirtyDayIndicesRef.current.add(di);
          }
        }
      }
      return next;
    });
  }, []);

  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1000);
  const { user } = useAuth();

  // Crew-mates overlay state
  const [showCrewmates, setShowCrewmates] = useState(false);
  const [crewOptions, setCrewOptions] = useState([]); // {id, name}
  const [crewFilterIds, setCrewFilterIds] = useState([]);
  const [crewmateCountsMap, setCrewmateCountsMap] = useState({}); // key: `${day}-${HH:MM}` -> {available, preferred}
  const [totalCrewmates, setTotalCrewmates] = useState(0);
  const [loadingCrewmates, setLoadingCrewmates] = useState(false);

  // Desktop grid drag state
  const [dragMode, setDragMode] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Calendar integration state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarIntegrations, setCalendarIntegrations] = useState([]); // Store actual integrations
  const [loadingCalendarEvents, setLoadingCalendarEvents] = useState(false);
  const [enabledCalendars, setEnabledCalendars] = useState(new Set()); // Track which calendars are enabled
  const [showCalendarView, setShowCalendarView] = useState(true); // Toggle for calendar view

  // Debounced save: wipe + replace full calendar dates for dirty days only (explicit unavailable persists).
  const saveAvailabilityFunction = useCallback(async () => {
    const currentWeekForSave = selectedWeekRef.current;
    const currentAvailability = availabilityRef.current;

    const dirtyIndices = [...dirtyDayIndicesRef.current];
    if (dirtyIndices.length === 0) {
      return;
    }

    const replace_calendar_dates = [
      ...new Set(
        dirtyIndices.map((dayIndex) => format(addDays(currentWeekForSave, dayIndex), 'yyyy-MM-dd'))
      ),
    ];

    const slots = [];
    for (const dayIndex of dirtyIndices) {
      const dayData = currentAvailability[dayIndex] || {};
      for (const [timeSlot, type] of Object.entries(dayData)) {
        if (type) {
          const date = format(addDays(currentWeekForSave, dayIndex), 'yyyy-MM-dd');
          const [hours, minutes] = timeSlot.split(':');

          const startMinutes = parseInt(hours) * 60 + parseInt(minutes);
          const endMinutes = startMinutes + 30;
          const endHours = Math.floor(endMinutes / 60);
          const endMins = endMinutes % 60;
          const endTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}:00`;

          slots.push({
            date,
            start_time: `${timeSlot}:00`,
            end_time: endTime,
            availability_type: type,
          });
        }
      }
    }

    console.log(
      'Saving availability (dirty days):',
      replace_calendar_dates.join(', '),
      'slots:',
      slots.length,
      'week:',
      format(currentWeekForSave, 'yyyy-MM-dd')
    );

    await availabilityAPI.setBulkAvailability(
      slots,
      null,
      null,
      replace_calendar_dates
    );

    dirtyDayIndicesRef.current.clear();

    console.log('Availability saved successfully, invalidating dashboard cache...');
    invalidateDashboard();
  }, [invalidateDashboard]);

  const { isSaving, lastSaved, hasUnsavedChanges, triggerSave, forceSave, resetState } =
    useDebouncedSave(saveAvailabilityFunction, 2000);

  // Notify parent component of save status changes
  useEffect(() => {
    if (onSaveStatusChange) {
      onSaveStatusChange({ isSaving, lastSaved, hasUnsavedChanges });
    }
  }, [isSaving, lastSaved, hasUnsavedChanges, onSaveStatusChange]);

  // Reset save state when loading new data
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use refs to always access current state in save function
  const availabilityRef = useRef(availability);
  const selectedWeekRef = useRef(selectedWeek);

  // Update refs when state changes
  useEffect(() => {
    availabilityRef.current = availability;
  }, [availability]);

  useEffect(() => {
    selectedWeekRef.current = selectedWeek;
  }, [selectedWeek]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1000);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSelectedWeek(currentWeek);
    setIsInitialLoad(true);

    // Check if we have copied availability in localStorage
    const storedCopiedData = localStorage.getItem('copiedAvailability');
    const isCopiedData = localStorage.getItem('isCopiedData') === 'true';

    if (storedCopiedData && isCopiedData) {
      try {
        const copiedData = JSON.parse(storedCopiedData);
        suppressDirtyTrackingRef.current = true;
        setAvailabilityRaw(copiedData);
        suppressDirtyTrackingRef.current = false;
        for (let i = 0; i < 7; i++) {
          dirtyDayIndicesRef.current.add(i);
        }
        setPreviousAvailability(copiedData);
        setIsInitialLoad(false);
        resetState();
        localStorage.removeItem('copiedAvailability'); // Clear the stored data
        localStorage.removeItem('isCopiedData'); // Clear the flag
        // Force save the copied data immediately
        setTimeout(() => {
          forceSave();
        }, 100);
      } catch (error) {
        console.error('Failed to parse copied availability:', error);
        localStorage.removeItem('copiedAvailability');
        localStorage.removeItem('isCopiedData');
        loadAvailabilityForWeek(currentWeek);
      }
    } else {
      loadAvailabilityForWeek(currentWeek);
    }

    // Also refresh crewmates for the new week
    loadCrewmatesForWeek(currentWeek, crewFilterIds);

    // Load calendar events for the new week
    loadCalendarEventsForWeek(currentWeek);

    // Load calendar integrations
    loadCalendarIntegrations();
  }, [currentWeek]);

  useEffect(() => {
    // Load user's crews to build filter options
    const loadCrews = async () => {
      try {
        const crews = await crewsAPI.getCrews();
        if (user) {
          const myCrews = (crews || []).filter((c) =>
            c.members?.some((m) => m.user_id === user.id)
          );
          setCrewOptions(myCrews.map((c) => ({ id: c.id, name: c.name })));
        } else {
          setCrewOptions([]);
        }
      } catch (e) {
        // non-fatal
      }
    };
    loadCrews();
  }, [user]);

  useEffect(() => {
    // Refetch crewmates overlay when filters change
    if (selectedWeek) {
      loadCrewmatesForWeek(selectedWeek, crewFilterIds);
    }
  }, [crewFilterIds, selectedWeek]);

  // Track previous availability to detect actual changes
  const [previousAvailability, setPreviousAvailability] = useState({});

  // Trigger debounced save when availability changes
  useEffect(() => {
    if (!loading && !isInitialLoad) {
      // Only trigger save if availability has actually changed
      const availabilityString = JSON.stringify(availability);
      const previousString = JSON.stringify(previousAvailability);

      if (availabilityString !== previousString) {
        setPreviousAvailability(availability);
        triggerSave();
      }
    }
  }, [availability, triggerSave, loading, isInitialLoad, previousAvailability]);

  const buildCountsMap = (response) => {
    const map = {};
    if (!response?.counts) return map;
    for (const item of response.counts) {
      const key = `${item.day_of_week}-${item.time_slot}`;
      map[key] = { available: item.available_count || 0, preferred: item.preferred_count || 0 };
    }
    return map;
  };

  const loadCrewmatesForWeek = async (week, crewIds = []) => {
    try {
      setLoadingCrewmates(true);
      const weekStart = format(week, 'yyyy-MM-dd');
      const data = await availabilityAPI.getCrewmatesWeeklyAvailability(weekStart, {
        crewIds,
      });
      setCrewmateCountsMap(buildCountsMap(data));
      setTotalCrewmates(data?.total_crewmates || 0);
    } catch (error) {
      // silent failure to not block availability view
    } finally {
      setLoadingCrewmates(false);
    }
  };

  const saveAvailability = async () => {
    try {
      await forceSave();
    } catch (error) {
      console.error('Failed to save availability:', error);
      showToast('Failed to save availability', 'error');
    }
  };

  const copyToNextWeek = async () => {
    const newAvailability = JSON.parse(JSON.stringify(availability));
    // Store copied data in localStorage to persist across navigation
    localStorage.setItem('copiedAvailability', JSON.stringify(newAvailability));
    // Add a flag to indicate this is copied data
    localStorage.setItem('isCopiedData', 'true');
    // Navigate to next week using parent's navigation
    if (onWeekChange) {
      onWeekChange(1);
    }
    showToast('Availability copied to next week', 'success');
  };

  const navigateWeek = (direction) => {
    const newWeek = addDays(selectedWeek, direction * 7);
    setSelectedWeek(newWeek);
    // Reload availability for the new week
    loadAvailabilityForWeek(newWeek);
    loadCrewmatesForWeek(newWeek, crewFilterIds);
  };

  const loadAvailabilityForWeek = async (week) => {
    try {
      setLoading(true);
      const weekStart = format(week, 'yyyy-MM-dd');
      const data = await availabilityAPI.getWeeklyAvailability(weekStart);

      // Expand each [start, end) row into 30-minute keys that exist on the grid (TIME_SLOTS).
      // Ranges starting before the first row (e.g. 06:00) still paint 07:00–09:30 correctly.
      const gridData = weeklyApiSlotsToGridAvailability(data, TIME_SLOTS);

      suppressDirtyTrackingRef.current = true;
      dirtyDayIndicesRef.current.clear();
      setAvailabilityRaw(gridData);
      suppressDirtyTrackingRef.current = false;
      setPreviousAvailability(gridData); // Set previous availability to current data
      setIsInitialLoad(false);
      resetState(); // Reset save state when data is loaded
    } catch (error) {
      console.error('Failed to load availability:', error);
      showToast('Failed to load availability', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCalendarEventsForWeek = async (week) => {
    try {
      setLoadingCalendarEvents(true);
      const weekStart = format(week, 'yyyy-MM-dd');
      const data = await calendarIntegrationAPI.getWeeklyEvents(weekStart);
      setCalendarEvents(data.events || []);
    } catch (error) {
      console.error('Failed to load calendar events:', error);
      // Don't show error toast for calendar events as they're optional
    } finally {
      setLoadingCalendarEvents(false);
    }
  };

  const loadCalendarIntegrations = async () => {
    try {
      const integrations = await calendarIntegrationAPI.getIntegrations();
      setCalendarIntegrations(integrations);
    } catch (error) {
      console.error('Failed to load calendar integrations:', error);
      // Don't show error toast for calendar integrations as they're optional
    }
  };

  // Toggle individual calendar visibility
  const toggleCalendar = useCallback((calendarName) => {
    console.log('toggleCalendar called with:', calendarName);
    setEnabledCalendars((prev) => {
      const newSet = new Set(prev);
      console.log('Previous enabled calendars:', Array.from(prev));
      if (newSet.has(calendarName)) {
        newSet.delete(calendarName);
        console.log('Removed calendar:', calendarName);
      } else {
        newSet.add(calendarName);
        console.log('Added calendar:', calendarName);
      }
      console.log('New enabled calendars:', Array.from(newSet));
      return newSet;
    });
  }, []);

  // Get filtered calendar events based on enabled calendars
  const filteredCalendarEvents = useMemo(() => {
    if (enabledCalendars.size === 0) {
      // If no calendars are explicitly enabled, show all
      return calendarEvents;
    }
    return calendarEvents.filter((event) => enabledCalendars.has(event.calendar_name));
  }, [calendarEvents, enabledCalendars]);

  const showNotification = (message, type = 'info') => {
    // Only show error notifications and copy success, remove other success/info toasts
    if (type === 'error' || (type === 'success' && message.includes('copied'))) {
      showToast(message, type);
    }
  };

  const commonProps = {
    availability,
    setAvailability,
    selectedWeek,
    loading,
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    triggerSave,
    navigateWeek,
    saveAvailability,
    copyToNextWeek,
    showNotification,
    dragMode,
    setDragMode,
    isDragging,
    setIsDragging,
    // Crew-mates overlay props
    showCrewmates,
    setShowCrewmates,
    crewOptions,
    crewFilterIds,
    setCrewFilterIds,
    crewmateCountsMap,
    totalCrewmates,
    loadingCrewmates,
    // Calendar integration props - shared between mobile and desktop
    calendarEvents,
    calendarIntegrations,
    filteredCalendarEvents,
    loadingCalendarEvents,
    enabledCalendars,
    toggleCalendar,
    // Calendar view toggle
    showCalendarView,
    setShowCalendarView,
  };

  const desktopProps = {
    ...commonProps,
    // Desktop-specific props can be added here if needed
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Loading availability...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isMobile ? (
        <MobileAvailabilityGrid {...commonProps} />
      ) : (
        <DesktopAvailabilityGrid {...desktopProps} />
      )}

      {/* Calendar Integration Modal - desktop only */}
      {/* This section is no longer needed as the modal is removed */}
    </div>
  );
};

export default AvailabilityGrid;
